/**
 * Zariya Theme — 3D Animation Engine v2
 * Tilt, parallax, scroll-reveal, particles, cursor
 * Mobile-optimized, seamless loading
 */
(function () {
  'use strict';

  const isMobile = () => window.innerWidth <= 768;
  const isTouch = () => 'ontouchstart' in window;
  const prefersReduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─── 1. TILT — 3D perspective on pointer (desktop only) ─── */
  const Tilt = {
    init(selector, opts = {}) {
      if (isMobile()) return;
      const c = Object.assign({ maxTilt: 10, perspective: 1000, scale: 1.02, speed: 400, glare: true, maxGlare: .15 }, opts);
      document.querySelectorAll(selector).forEach(el => {
        // Skip cards with 3D product view to avoid conflict
        if (el.querySelector('[data-z3d-view]') && c.maxTilt > 6) return;
        this._attach(el, c);
      });
    },
    _attach(el, c) {
      el.style.transformStyle = 'preserve-3d';
      el.style.transition = `transform ${c.speed}ms cubic-bezier(.03,.98,.52,.99)`;
      let glare = null;
      if (c.glare) {
        glare = document.createElement('div');
        glare.className = 'zariya-glare';
        if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
        el.appendChild(glare);
      }
      el.addEventListener('mousemove', e => {
        const r = el.getBoundingClientRect();
        const x = e.clientX - r.left, y = e.clientY - r.top;
        const cx = r.width / 2, cy = r.height / 2;
        const tX = ((y - cy) / cy) * c.maxTilt;
        const tY = ((cx - x) / cx) * c.maxTilt;
        el.style.transform = `perspective(${c.perspective}px) rotateX(${tX}deg) rotateY(${tY}deg) scale3d(${c.scale},${c.scale},${c.scale})`;
        if (glare) {
          const a = Math.atan2(y - cy, x - cx) * 180 / Math.PI + 90;
          glare.style.background = `linear-gradient(${a}deg,rgba(255,255,255,0) 40%,rgba(255,255,255,${c.maxGlare * (Math.abs(tX) + Math.abs(tY)) / (c.maxTilt * 2)}) 100%)`;
          glare.style.opacity = '1';
        }
      }, { passive: true });
      el.addEventListener('mouseleave', () => {
        el.style.transform = `perspective(${c.perspective}px) rotateX(0) rotateY(0) scale3d(1,1,1)`;
        if (glare) glare.style.opacity = '0';
      });
    }
  };

  /* ─── 3. PARALLAX — scroll depth (desktop only) ─── */
  const Parallax = {
    items: [],
    init() {
      if (isMobile()) return;
      const hero = document.querySelector('.hero-static-img');
      if (hero) this.items.push({ el: hero, speed: 0.25 });
      document.querySelectorAll('.collection-showcase-header, .shop-categories-header, .brand-pillars-header').forEach(el => {
        this.items.push({ el, speed: 0.05 });
      });
      if (this.items.length) {
        let ticking = false;
        window.addEventListener('scroll', () => {
          if (!ticking) { requestAnimationFrame(() => { this._tick(); ticking = false; }); ticking = true; }
        }, { passive: true });
      }
    },
    _tick() {
      const sy = window.scrollY;
      this.items.forEach(({ el, speed }) => {
        const r = el.getBoundingClientRect();
        const center = r.top + r.height / 2 + sy;
        el.style.transform = `translateY(${(sy - center + window.innerHeight / 2) * speed}px)`;
      });
    }
  };

  /* ─── 4. MAGNETIC — pull toward cursor (desktop only) ─── */
  const Magnetic = {
    init(selector, str = 0.25) {
      if (isMobile()) return;
      document.querySelectorAll(selector).forEach(el => {
        el.style.transition = 'transform .4s cubic-bezier(.23,1,.32,1)';
        el.addEventListener('mousemove', e => {
          const r = el.getBoundingClientRect();
          el.style.transform = `translate3d(${(e.clientX - r.left - r.width / 2) * str}px,${(e.clientY - r.top - r.height / 2) * str}px,0)`;
        }, { passive: true });
        el.addEventListener('mouseleave', () => { el.style.transform = 'translate3d(0,0,0)'; });
      });
    }
  };

  /* ─── 5. SCROLL REVEAL 3D ─── */
  const ScrollReveal3D = {
    init() {
      this._tag();
      const obs = new IntersectionObserver(entries => {
        entries.forEach(e => {
          if (e.isIntersecting) { e.target.classList.add('z3d-on'); obs.unobserve(e.target); }
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -30px 0px' });
      document.querySelectorAll('.z3d-reveal').forEach(el => obs.observe(el));
    },
    _tag() {
      const delay = (el, i, base = 0.08) => { el.style.transitionDelay = `${i * base}s`; };

      document.querySelectorAll('.brand-pillar-card').forEach((el, i) => {
        el.classList.add('z3d-reveal', 'z3d-flip-up'); delay(el, i, 0.1);
      });
      document.querySelectorAll('.collection-showcase-item').forEach((el, i) => {
        el.classList.add('z3d-reveal', i % 2 === 0 ? 'z3d-slide-left' : 'z3d-slide-right'); delay(el, i, 0.12);
      });
      document.querySelectorAll('.shop-category-tile').forEach((el, i) => {
        el.classList.add('z3d-reveal', 'z3d-scale-up'); delay(el, i, 0.06);
      });
      // Product cards — lighter animation on mobile
      document.querySelectorAll('.featured-grid .product-card, .product-grid .product-card').forEach((el, i) => {
        el.classList.add('z3d-reveal', isMobile() ? 'z3d-fade-up' : 'z3d-rise');
        delay(el, i % 4, 0.06);
      });
      document.querySelectorAll('.collection-showcase-title, .shop-categories-heading, .lookbook-heading').forEach(el => {
        el.classList.add('z3d-reveal', 'z3d-text-reveal');
      });
      document.querySelectorAll('.lookbook-card').forEach((el, i) => {
        el.classList.add('z3d-reveal', 'z3d-depth-slide'); delay(el, i, 0.12);
      });
      document.querySelectorAll('.hero-category-card').forEach((el, i) => {
        el.classList.add('z3d-reveal', 'z3d-scale-up'); delay(el, i, 0.08);
      });
      document.querySelectorAll('.lookbook-stat').forEach((el, i) => {
        el.classList.add('z3d-reveal', 'z3d-flip-up'); delay(el, i, 0.08);
      });
      // Section glows
      document.querySelectorAll('.featured-products-section, .lookbook-banner, .collection-showcase-section, .shop-categories-section').forEach(el => {
        el.classList.add('z3d-section-glow');
      });
    }
  };

  /* ─── 6. HERO PARTICLES — canvas (desktop + tablet) ─── */
  const Particles = {
    init() {
      const hero = document.querySelector('.hero-static-image');
      if (!hero) return;
      const count = isMobile() ? 25 : 55;
      const c = document.createElement('canvas');
      c.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:2;opacity:.35';
      hero.appendChild(c);
      const ctx = c.getContext('2d');
      let W, H, ps = [], id;
      const rand = (a, b) => Math.random() * (b - a) + a;
      const make = () => ({ x: rand(0, W), y: rand(0, H), r: rand(.8, 3), vx: rand(-.2, .2), vy: rand(-.4, -.08), a: rand(.1, .5), life: rand(.003, .006), col: Math.random() < .5 ? '201,169,98' : '255,255,255' });
      const resize = () => { W = c.width = hero.offsetWidth; H = c.height = hero.offsetHeight; };
      const draw = () => {
        ctx.clearRect(0, 0, W, H);
        ps.forEach((p, i) => {
          p.x += p.vx; p.y += p.vy; p.a -= p.life;
          if (p.a <= 0 || p.y < -10) { ps[i] = make(); ps[i].y = H + 5; }
          ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${p.col},${Math.max(0, p.a)})`; ctx.fill();
        });
        id = requestAnimationFrame(draw);
      };
      new IntersectionObserver(([e]) => {
        if (e.isIntersecting) { resize(); ps = Array.from({ length: count }, make); draw(); }
        else cancelAnimationFrame(id);
      }, { threshold: 0 }).observe(hero);
      window.addEventListener('resize', resize, { passive: true });
    }
  };

  /* ─── 7. CUSTOM CURSOR (desktop only) ─── */
  const Cursor = {
    init() {
      if (isMobile() || isTouch()) return;
      const dot = document.createElement('div'); dot.className = 'z3d-cursor-dot';
      const ring = document.createElement('div'); ring.className = 'z3d-cursor-ring';
      document.body.appendChild(dot); document.body.appendChild(ring);
      let mx = -100, my = -100, dx = -100, dy = -100;
      document.addEventListener('mousemove', e => { mx = e.clientX; my = e.clientY; }, { passive: true });
      const hoverSel = 'a, button, .product-card, .shop-category-tile, .collection-showcase-item';
      document.addEventListener('mouseover', e => { if (e.target.closest(hoverSel)) ring.classList.add('z3d-hover'); });
      document.addEventListener('mouseout', e => { if (e.target.closest(hoverSel)) ring.classList.remove('z3d-hover'); });
      const tick = () => {
        dx += (mx - dx) * .12; dy += (my - dy) * .12;
        dot.style.transform = `translate(${mx - 3}px,${my - 3}px)`;
        ring.style.left = `${dx}px`; ring.style.top = `${dy}px`;
        requestAnimationFrame(tick);
      };
      tick();
    }
  };

  /* ─── 8. NUMBER COUNTER ─── */
  const Counter = {
    init() {
      const obs = new IntersectionObserver(entries => {
        entries.forEach(e => {
          if (!e.isIntersecting) return;
          const el = e.target, txt = el.textContent.trim(), n = parseInt(txt);
          if (isNaN(n)) return;
          let cur = 0; const steps = 25, step = n / steps;
          const timer = setInterval(() => {
            cur = Math.min(cur + step, n);
            el.textContent = String(Math.round(cur)).padStart(txt.length, '0');
            if (cur >= n) clearInterval(timer);
          }, 40);
          obs.unobserve(el);
        });
      }, { threshold: .4 });
      document.querySelectorAll('.collection-showcase-number, .pillar-number').forEach(el => obs.observe(el));
    }
  };

  /* ─── 9. CARD SHINE INJECT ─── */
  const Shine = {
    init() {
      if (isMobile()) return;
      document.querySelectorAll('.product-card-media').forEach(m => {
        if (m.querySelector('.card-shine-sweep')) return;
        const s = document.createElement('div'); s.className = 'card-shine-sweep';
        m.style.position = 'relative'; m.appendChild(s);
      });
    }
  };

  /* ─── 10. SUPERDESIGN CUSTOMIZER ─── */
  const SuperDesign = {
    init(opts = {}) {
      const root = document.querySelector('[data-superdesign]');
      if (!root) return;

      const reducedMotion = !!opts.reducedMotion;
      const stage = root.querySelector('[data-superdesign-stage]');
      const model = root.querySelector('[data-superdesign-model]');
      if (!stage || !model) return;

      const colorInputs = root.querySelectorAll('input[name="superdesign-color"]');
      const colorPicker = root.querySelector('[data-superdesign-color-picker]');
      const colorHexInput = root.querySelector('[data-superdesign-color-hex]');
      const fitSelect = root.querySelector('[data-superdesign-fit]');
      const styleInputs = root.querySelectorAll('input[name="superdesign-style"]');
      const placementSelect = root.querySelector('[data-superdesign-placement]');
      const uploadTrigger = root.querySelector('[data-superdesign-upload-trigger]');
      const uploadInput = root.querySelector('[data-superdesign-upload]');
      const fileName = root.querySelector('[data-superdesign-file-name]');
      const fitLabel = root.querySelector('[data-superdesign-fit-label]');
      const styleLabel = root.querySelector('[data-superdesign-style-label]');
      const colorLabel = root.querySelector('[data-superdesign-color-label]');
      const frontPrint = root.querySelector('[data-superdesign-print-front]');
      const backPrint = root.querySelector('[data-superdesign-print-back]');
      const posXInput = root.querySelector('[data-superdesign-pos-x]');
      const posYInput = root.querySelector('[data-superdesign-pos-y]');
      const scaleInput = root.querySelector('[data-superdesign-scale]');
      const rotateInput = root.querySelector('[data-superdesign-rotate]');
      const artworkResetButton = root.querySelector('[data-superdesign-artwork-reset]');
      const backLink = root.querySelector('[data-superdesign-back]');

      const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
      const toNumber = (value, fallback) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
      };
      const normalizeHex = (value) => {
        const input = String(value || '').trim();
        if (!input) return '';
        const withHash = input.startsWith('#') ? input : `#${input}`;
        if (!/^#[0-9a-fA-F]{6}$/.test(withHash)) return '';
        return withHash.toUpperCase();
      };
      const basePose = { x: -10, y: -18 };
      const artworkBounds = {
        xMin: -110,
        xMax: 110,
        yMin: -130,
        yMax: 130,
        scaleMin: 0.4,
        scaleMax: 2.2,
        rotateMin: -180,
        rotateMax: 180
      };

      let targetX = basePose.x;
      let targetY = basePose.y;
      let currentX = targetX;
      let currentY = targetY;
      let fitScale = 1;
      let rafId = null;
      let rotateDragActive = false;
      let rotateDragX = 0;
      let rotateDragY = 0;
      let artworkDragActive = false;
      let artworkDragX = 0;
      let artworkDragY = 0;
      let artworkStartX = 0;
      let artworkStartY = 0;
      let artworkUrl = '';
      const artwork = {
        x: 0,
        y: 0,
        scale: 1,
        rotate: 0
      };

      const fitText = {
        regular: 'Regular',
        oversized: 'Oversized'
      };

      const styleText = {
        'round-neck': 'Round neck',
        polo: 'Polo',
        sweatshirt: 'Sweatshirt',
        hoodie: 'Hoodie'
      };

      const render = () => {
        model.style.transform = `rotateX(${currentX.toFixed(2)}deg) rotateY(${currentY.toFixed(2)}deg) scale(${fitScale})`;
      };

      const tick = () => {
        currentX += (targetX - currentX) * 0.14;
        currentY += (targetY - currentY) * 0.14;
        render();
        rafId = window.requestAnimationFrame(tick);
      };

      const setColor = ({ hex, name, wash, syncInputs = true }) => {
        const normalized = normalizeHex(hex);
        if (!normalized) return;
        root.style.setProperty('--superdesign-color', normalized);
        root.dataset.wash = wash || 'solid';
        if (colorLabel) colorLabel.textContent = name || normalized;
        if (syncInputs) {
          if (colorPicker) colorPicker.value = normalized.toLowerCase();
          if (colorHexInput) colorHexInput.value = normalized;
        }
      };

      const setFit = (value) => {
        const fit = value === 'oversized' ? 'oversized' : 'regular';
        root.dataset.fit = fit;
        fitScale = fit === 'oversized' ? 1.08 : 1;
        if (fitLabel) fitLabel.textContent = fitText[fit];
        if (reducedMotion) {
          currentX = targetX;
          currentY = targetY;
          render();
        }
      };

      const setStyle = (value) => {
        const style = styleText[value] ? value : 'round-neck';
        root.dataset.style = style;
        if (styleLabel) styleLabel.textContent = styleText[style];
      };

      const syncArtworkControls = () => {
        if (posXInput) posXInput.value = String(Math.round(artwork.x));
        if (posYInput) posYInput.value = String(Math.round(artwork.y));
        if (scaleInput) scaleInput.value = String(Math.round(artwork.scale * 100));
        if (rotateInput) rotateInput.value = String(Math.round(artwork.rotate));
      };

      const applyArtworkTransform = () => {
        const transform = `translate(-50%, -50%) translate(${artwork.x}px, ${artwork.y}px) scale(${artwork.scale}) rotate(${artwork.rotate}deg)`;
        [frontPrint, backPrint].forEach((print) => {
          if (!print) return;
          print.style.transform = transform;
        });
        syncArtworkControls();
      };

      const resetArtwork = () => {
        artwork.x = 0;
        artwork.y = 0;
        artwork.scale = 1;
        artwork.rotate = 0;
        applyArtworkTransform();
      };

      const setArtworkFromControls = () => {
        artwork.x = clamp(toNumber(posXInput ? posXInput.value : 0, 0), artworkBounds.xMin, artworkBounds.xMax);
        artwork.y = clamp(toNumber(posYInput ? posYInput.value : 0, 0), artworkBounds.yMin, artworkBounds.yMax);
        artwork.scale = clamp(toNumber(scaleInput ? scaleInput.value : 100, 100) / 100, artworkBounds.scaleMin, artworkBounds.scaleMax);
        artwork.rotate = clamp(toNumber(rotateInput ? rotateInput.value : 0, 0), artworkBounds.rotateMin, artworkBounds.rotateMax);
        applyArtworkTransform();
      };

      const hidePrint = (el) => {
        if (!el) return;
        el.classList.remove('is-visible', 'is-dragging');
        el.removeAttribute('src');
      };

      const showPrint = (el, src) => {
        if (!el) return;
        el.src = src;
        el.classList.add('is-visible');
      };

      const applyArtworkPlacement = () => {
        if (!artworkUrl) {
          hidePrint(frontPrint);
          hidePrint(backPrint);
          applyArtworkTransform();
          return;
        }

        const placement = placementSelect ? placementSelect.value : 'front';
        const showFront = placement === 'front' || placement === 'both';
        const showBack = placement === 'back' || placement === 'both';

        if (showFront) showPrint(frontPrint, artworkUrl);
        else hidePrint(frontPrint);

        if (showBack) showPrint(backPrint, artworkUrl);
        else hidePrint(backPrint);

        applyArtworkTransform();
      };

      const updateTargetFromPointer = (clientX, clientY) => {
        const rect = stage.getBoundingClientRect();
        const px = (clientX - rect.left) / rect.width - 0.5;
        const py = (clientY - rect.top) / rect.height - 0.5;
        targetY = clamp(basePose.y + px * 50, -64, 64);
        targetX = clamp(basePose.x - py * 34, -40, 40);
      };

      const stopRotateDragging = () => {
        if (!rotateDragActive) return;
        rotateDragActive = false;
        stage.classList.remove('is-dragging');
      };

      const stopArtworkDragging = () => {
        if (!artworkDragActive) return;
        artworkDragActive = false;
        stage.classList.remove('is-artwork-dragging');
        [frontPrint, backPrint].forEach((print) => {
          if (print) print.classList.remove('is-dragging');
        });
      };

      const startArtworkDragging = (event) => {
        const print = event.currentTarget;
        if (!print || !print.classList.contains('is-visible')) return;
        artworkDragActive = true;
        artworkDragX = event.clientX;
        artworkDragY = event.clientY;
        artworkStartX = artwork.x;
        artworkStartY = artwork.y;
        stage.classList.add('is-artwork-dragging');
        print.classList.add('is-dragging');
        if (print.setPointerCapture) print.setPointerCapture(event.pointerId);
        event.stopPropagation();
        event.preventDefault();
      };

      [frontPrint, backPrint].forEach((print) => {
        if (!print) return;
        print.addEventListener('pointerdown', startArtworkDragging);
      });

      stage.addEventListener('pointerdown', (event) => {
        if (event.target.closest('.superdesign-print.is-visible')) return;
        rotateDragActive = true;
        rotateDragX = event.clientX;
        rotateDragY = event.clientY;
        stage.classList.add('is-dragging');
        if (stage.setPointerCapture) stage.setPointerCapture(event.pointerId);
      });

      stage.addEventListener('pointermove', (event) => {
        if (artworkDragActive) return;
        if (rotateDragActive) {
          const deltaX = event.clientX - rotateDragX;
          const deltaY = event.clientY - rotateDragY;
          rotateDragX = event.clientX;
          rotateDragY = event.clientY;
          targetY = clamp(targetY + deltaX * 0.45, -75, 75);
          targetX = clamp(targetX - deltaY * 0.35, -45, 45);
        } else {
          updateTargetFromPointer(event.clientX, event.clientY);
        }

        if (reducedMotion) {
          currentX = targetX;
          currentY = targetY;
          render();
        }
      }, { passive: true });

      window.addEventListener('pointermove', (event) => {
        if (!artworkDragActive) return;
        const deltaX = event.clientX - artworkDragX;
        const deltaY = event.clientY - artworkDragY;
        artwork.x = clamp(artworkStartX + deltaX, artworkBounds.xMin, artworkBounds.xMax);
        artwork.y = clamp(artworkStartY + deltaY, artworkBounds.yMin, artworkBounds.yMax);
        applyArtworkTransform();
      }, { passive: true });

      stage.addEventListener('pointerleave', () => {
        if (rotateDragActive || artworkDragActive) return;
        targetX = basePose.x;
        targetY = basePose.y;
        if (reducedMotion) {
          currentX = targetX;
          currentY = targetY;
          render();
        }
      });

      stage.addEventListener('pointerup', stopRotateDragging);
      stage.addEventListener('pointercancel', stopRotateDragging);
      window.addEventListener('pointerup', () => {
        stopRotateDragging();
        stopArtworkDragging();
      });
      window.addEventListener('pointercancel', () => {
        stopRotateDragging();
        stopArtworkDragging();
      });

      colorInputs.forEach(input => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          setColor({
            hex: input.value,
            name: input.dataset.colorName || input.value,
            wash: input.dataset.wash || 'solid'
          });
        });
      });

      const clearPresetSelection = () => {
        colorInputs.forEach((input) => {
          input.checked = false;
        });
      };

      if (colorPicker) {
        colorPicker.addEventListener('input', () => {
          const customColor = normalizeHex(colorPicker.value);
          if (!customColor) return;
          clearPresetSelection();
          setColor({ hex: customColor, name: 'Custom', wash: 'solid', syncInputs: false });
          if (colorHexInput) colorHexInput.value = customColor;
          colorPicker.value = customColor.toLowerCase();
        });
      }

      if (colorHexInput) {
        const applyHexField = () => {
          const customColor = normalizeHex(colorHexInput.value);
          if (!customColor) {
            colorHexInput.value = colorPicker ? colorPicker.value.toUpperCase() : '#111111';
            return;
          }
          clearPresetSelection();
          setColor({ hex: customColor, name: 'Custom', wash: 'solid', syncInputs: false });
          if (colorPicker) colorPicker.value = customColor.toLowerCase();
          colorHexInput.value = customColor;
        };

        colorHexInput.addEventListener('change', applyHexField);
        colorHexInput.addEventListener('blur', applyHexField);
        colorHexInput.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          applyHexField();
        });
      }

      const checkedColor = root.querySelector('input[name="superdesign-color"]:checked');
      if (checkedColor) {
        setColor({
          hex: checkedColor.value,
          name: checkedColor.dataset.colorName || checkedColor.value,
          wash: checkedColor.dataset.wash || 'solid'
        });
      }

      if (fitSelect) {
        fitSelect.addEventListener('change', () => setFit(fitSelect.value));
        setFit(fitSelect.value);
      } else {
        setFit(root.dataset.fit);
      }

      styleInputs.forEach(input => {
        input.addEventListener('change', () => {
          if (input.checked) setStyle(input.value);
        });
      });

      const checkedStyle = root.querySelector('input[name="superdesign-style"]:checked');
      if (checkedStyle) setStyle(checkedStyle.value);

      if (uploadTrigger && uploadInput) {
        uploadTrigger.addEventListener('click', () => uploadInput.click());
      }

      if (placementSelect) {
        placementSelect.addEventListener('change', applyArtworkPlacement);
      }

      [posXInput, posYInput, scaleInput, rotateInput].forEach((control) => {
        if (!control) return;
        control.addEventListener('input', setArtworkFromControls);
      });

      if (artworkResetButton) {
        artworkResetButton.addEventListener('click', resetArtwork);
      }

      if (uploadInput) {
        uploadInput.addEventListener('change', () => {
          const file = uploadInput.files && uploadInput.files[0];
          if (!file) return;

          if (artworkUrl) URL.revokeObjectURL(artworkUrl);
          artworkUrl = URL.createObjectURL(file);

          if (fileName) fileName.textContent = file.name;
          applyArtworkPlacement();
        });
      }

      resetArtwork();
      applyArtworkPlacement();

      if (backLink) {
        backLink.addEventListener('click', (event) => {
          if (window.history.length > 1) {
            event.preventDefault();
            window.history.back();
          }
        });
      }

      if (reducedMotion) {
        render();
      } else {
        rafId = window.requestAnimationFrame(tick);
      }

      window.addEventListener('pagehide', () => {
        if (rafId) window.cancelAnimationFrame(rafId);
        if (artworkUrl) URL.revokeObjectURL(artworkUrl);
      }, { once: true });
    }
  };

  function initStudioV2() {
    const roots = document.querySelectorAll('[data-studio-root]');
    if (!roots.length) return;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const normalizeHex = (value) => {
      const raw = String(value || '').trim();
      if (!raw) return '#000000';
      const withHash = raw.startsWith('#') ? raw : `#${raw}`;
      return /^#[0-9a-fA-F]{6}$/.test(withHash) ? withHash.toUpperCase() : '#000000';
    };
    const hexToRgb = (hex) => {
      const clean = normalizeHex(hex).replace('#', '');
      return {
        r: parseInt(clean.slice(0, 2), 16),
        g: parseInt(clean.slice(2, 4), 16),
        b: parseInt(clean.slice(4, 6), 16)
      };
    };
    const rgba = (hex, alpha) => {
      const rgb = hexToRgb(hex);
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    };
    const washLabel = (wash) => ({
      'acid-blue': 'Acid Wash Blue',
      'acid-black': 'Washed Black',
      'acid-green': 'Acid Wash Green',
      'acid-red': 'Acid Wash Red',
      solid: 'Solid'
    }[wash] || 'Solid');

    const drawShirtPath = (ctx, width, height) => {
      ctx.beginPath();
      ctx.moveTo(width * 0.32, height * 0.22);
      ctx.lineTo(width * 0.18, height * 0.30);
      ctx.quadraticCurveTo(width * 0.12, height * 0.33, width * 0.15, height * 0.40);
      ctx.lineTo(width * 0.22, height * 0.88);
      ctx.quadraticCurveTo(width * 0.24, height * 0.93, width * 0.30, height * 0.93);
      ctx.lineTo(width * 0.70, height * 0.93);
      ctx.quadraticCurveTo(width * 0.76, height * 0.93, width * 0.78, height * 0.88);
      ctx.lineTo(width * 0.85, height * 0.40);
      ctx.quadraticCurveTo(width * 0.88, height * 0.33, width * 0.82, height * 0.30);
      ctx.lineTo(width * 0.68, height * 0.22);
      ctx.quadraticCurveTo(width * 0.62, height * 0.17, width * 0.56, height * 0.17);
      ctx.lineTo(width * 0.44, height * 0.17);
      ctx.quadraticCurveTo(width * 0.38, height * 0.17, width * 0.32, height * 0.22);
      ctx.closePath();
    };

    const drawWashOverlay = (ctx, width, height, wash) => {
      if (wash === 'solid') return;
      const blobs = {
        'acid-blue': [
          { x: 0.28, y: 0.30, r: 0.18, c: 'rgba(224,236,255,.42)' },
          { x: 0.66, y: 0.63, r: 0.21, c: 'rgba(32,48,79,.36)' },
          { x: 0.46, y: 0.46, r: 0.17, c: 'rgba(157,182,214,.26)' }
        ],
        'acid-black': [
          { x: 0.28, y: 0.31, r: 0.18, c: 'rgba(235,235,235,.30)' },
          { x: 0.66, y: 0.62, r: 0.22, c: 'rgba(8,9,11,.45)' },
          { x: 0.45, y: 0.48, r: 0.16, c: 'rgba(170,176,188,.22)' }
        ],
        'acid-green': [
          { x: 0.28, y: 0.30, r: 0.18, c: 'rgba(232,240,216,.35)' },
          { x: 0.66, y: 0.64, r: 0.22, c: 'rgba(45,63,38,.38)' },
          { x: 0.46, y: 0.45, r: 0.16, c: 'rgba(199,214,180,.24)' }
        ],
        'acid-red': [
          { x: 0.27, y: 0.31, r: 0.18, c: 'rgba(255,223,225,.38)' },
          { x: 0.68, y: 0.63, r: 0.22, c: 'rgba(91,29,39,.40)' },
          { x: 0.46, y: 0.46, r: 0.16, c: 'rgba(221,159,169,.24)' }
        ]
      }[wash];
      if (!blobs) return;

      blobs.forEach((blob) => {
        const gradient = ctx.createRadialGradient(
          width * blob.x,
          height * blob.y,
          0,
          width * blob.x,
          height * blob.y,
          width * blob.r
        );
        gradient.addColorStop(0, blob.c);
        gradient.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(width * blob.x, height * blob.y, width * blob.r, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const drawArtwork = (ctx, side, state, width, height) => {
      const shouldRender = state.placement === 'both' || state.placement === side;
      const centerX = width * 0.5;
      const centerY = side === 'front' ? height * 0.52 : height * 0.50;
      const maxW = width * 0.40 * state.scale;
      const maxH = height * 0.33 * state.scale;

      if (!shouldRender) {
        ctx.fillStyle = 'rgba(0,0,0,.38)';
        ctx.font = '600 16px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('No print', centerX, centerY);
        return;
      }

      if (!state.artworkImage) {
        ctx.strokeStyle = 'rgba(0,0,0,.35)';
        ctx.lineWidth = 2;
        ctx.setLineDash([8, 6]);
        ctx.strokeRect(centerX - maxW / 2, centerY - maxH / 2, maxW, maxH);
        ctx.setLineDash([]);
        ctx.fillStyle = 'rgba(0,0,0,.42)';
        ctx.font = '600 14px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Upload artwork', centerX, centerY + 6);
        return;
      }

      const image = state.artworkImage;
      const ratio = image.width / image.height;
      let drawW = maxW;
      let drawH = maxH;
      if (ratio > 1) drawH = maxW / ratio;
      else drawW = maxH * ratio;

      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,.25)';
      ctx.shadowBlur = 12;
      ctx.drawImage(image, centerX - drawW / 2, centerY - drawH / 2, drawW, drawH);
      ctx.restore();
    };

    const drawPreview = (canvas, side, state) => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const width = canvas.width;
      const height = canvas.height;

      ctx.clearRect(0, 0, width, height);
      const background = ctx.createLinearGradient(0, 0, 0, height);
      background.addColorStop(0, '#fdfdfd');
      background.addColorStop(1, '#efefef');
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, width, height);

      drawShirtPath(ctx, width, height);
      ctx.save();
      ctx.clip();
      ctx.fillStyle = normalizeHex(state.color);
      ctx.fillRect(0, 0, width, height);
      drawWashOverlay(ctx, width, height, state.wash);

      const fabricLight = ctx.createLinearGradient(width * 0.22, height * 0.15, width * 0.8, height * 0.88);
      fabricLight.addColorStop(0, rgba(state.color, 0.26));
      fabricLight.addColorStop(0.45, 'rgba(255,255,255,0)');
      fabricLight.addColorStop(1, 'rgba(0,0,0,.24)');
      ctx.fillStyle = fabricLight;
      ctx.fillRect(0, 0, width, height);

      drawArtwork(ctx, side, state, width, height);
      ctx.restore();

      drawShirtPath(ctx, width, height);
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(0,0,0,.18)';
      ctx.stroke();

      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      if (side === 'front') ctx.ellipse(width * 0.5, height * 0.23, width * 0.11, height * 0.05, 0, 0, Math.PI * 2);
      else ctx.ellipse(width * 0.5, height * 0.21, width * 0.09, height * 0.035, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      ctx.strokeStyle = 'rgba(255,255,255,.58)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      if (side === 'front') ctx.ellipse(width * 0.5, height * 0.23, width * 0.11, height * 0.05, 0, 0, Math.PI * 2);
      else ctx.ellipse(width * 0.5, height * 0.21, width * 0.09, height * 0.035, 0, 0, Math.PI * 2);
      ctx.stroke();

      ctx.fillStyle = 'rgba(18,18,18,.6)';
      ctx.font = '600 16px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(side === 'front' ? 'Front view' : 'Back view', width / 2, height - 18);
    };

    const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Unable to generate snapshot image.'));
          return;
        }
        resolve(blob);
      }, 'image/png', 0.92);
    });

    const assignFileInput = (input, file) => {
      if (!input) throw new Error('Snapshot input missing.');
      if (typeof DataTransfer === 'undefined') throw new Error('Browser does not support snapshot upload.');
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      input.files = dataTransfer.files;
    };

    roots.forEach((root) => {
      if (root.dataset.studioReady === 'true') return;
      root.dataset.studioReady = 'true';

      const form = root.querySelector('[data-studio-form]');
      const frontCanvas = root.querySelector('[data-studio-canvas="front"]');
      const backCanvas = root.querySelector('[data-studio-canvas="back"]');
      const swatches = root.querySelectorAll('[data-studio-swatch]');
      const uploadInput = root.querySelector('[data-studio-upload]');
      const placementSelect = root.querySelector('[data-studio-placement]');
      const scaleInput = root.querySelector('[data-studio-scale]');
      const scaleReadout = root.querySelector('[data-studio-scale-readout]');
      const colorReadout = root.querySelector('[data-studio-color-readout]');
      const washReadout = root.querySelector('[data-studio-wash-readout]');
      const fileReadout = root.querySelector('[data-studio-file-readout]');
      const statusReadout = root.querySelector('[data-studio-status]');
      const colorProperty = root.querySelector('[data-studio-color-property]');
      const washProperty = root.querySelector('[data-studio-wash-property]');
      const scaleProperty = root.querySelector('[data-studio-scale-property]');
      const artworkNameProperty = root.querySelector('[data-studio-artwork-name-property]');
      const variantSelect = root.querySelector('[data-studio-variant-select]');
      const variantIdInput = root.querySelector('[data-studio-variant-id]');
      const submitButton = root.querySelector('[data-studio-submit]');
      const priceReadout = root.querySelector('[data-studio-price]');
      const frontShotInput = root.querySelector('[data-studio-front-shot]');
      const backShotInput = root.querySelector('[data-studio-back-shot]');

      if (!form || !frontCanvas || !backCanvas || !placementSelect || !scaleInput || !uploadInput) return;

      const state = {
        color: normalizeHex(root.dataset.defaultColor || '#30406F'),
        colorName: root.dataset.defaultColorName || 'Acid Wash Blue',
        wash: root.dataset.defaultWash || 'acid-blue',
        placement: placementSelect.value || 'both',
        scale: Number(scaleInput.value || 100) / 100,
        artworkImage: null,
        artworkFileName: ''
      };
      let artworkUrl = '';

      const syncProperties = () => {
        if (colorProperty) colorProperty.value = `${state.colorName} (${state.color})`;
        if (washProperty) washProperty.value = washLabel(state.wash);
        if (scaleProperty) scaleProperty.value = `${Math.round(state.scale * 100)}%`;
        if (artworkNameProperty) artworkNameProperty.value = state.artworkFileName || '';
      };

      const syncReadouts = () => {
        if (colorReadout) colorReadout.textContent = state.colorName;
        if (washReadout) washReadout.textContent = washLabel(state.wash);
        if (fileReadout) fileReadout.textContent = state.artworkFileName || 'No file uploaded';
        if (scaleReadout) scaleReadout.textContent = `${Math.round(state.scale * 100)}%`;
      };

      const redraw = () => {
        drawPreview(frontCanvas, 'front', state);
        drawPreview(backCanvas, 'back', state);
      };

      const syncVariant = () => {
        if (!variantSelect || !variantIdInput) return;
        const option = variantSelect.options[variantSelect.selectedIndex];
        if (!option) return;
        variantIdInput.value = option.value;
        if (priceReadout && option.dataset.price) priceReadout.textContent = option.dataset.price;
        if (submitButton) submitButton.disabled = option.dataset.available !== 'true';
      };

      swatches.forEach((button) => {
        button.addEventListener('click', () => {
          swatches.forEach((other) => other.classList.remove('is-active'));
          button.classList.add('is-active');
          state.color = normalizeHex(button.dataset.color || state.color);
          state.colorName = button.dataset.colorName || state.colorName;
          state.wash = button.dataset.wash || 'solid';
          syncProperties();
          syncReadouts();
          redraw();
        });
      });

      placementSelect.addEventListener('change', () => {
        state.placement = placementSelect.value || 'both';
        redraw();
      });

      scaleInput.addEventListener('input', () => {
        state.scale = clamp(Number(scaleInput.value || 100) / 100, 0.7, 1.5);
        syncProperties();
        syncReadouts();
        redraw();
      });

      uploadInput.addEventListener('change', () => {
        const file = uploadInput.files && uploadInput.files[0];
        if (!file) {
          state.artworkImage = null;
          state.artworkFileName = '';
          syncProperties();
          syncReadouts();
          redraw();
          return;
        }

        if (!file.type || file.type.indexOf('image/') !== 0) {
          uploadInput.value = '';
          state.artworkImage = null;
          state.artworkFileName = '';
          if (statusReadout) statusReadout.textContent = 'Please upload a valid image file.';
          syncProperties();
          syncReadouts();
          redraw();
          return;
        }

        const nextUrl = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
          if (artworkUrl) URL.revokeObjectURL(artworkUrl);
          artworkUrl = nextUrl;
          state.artworkImage = image;
          state.artworkFileName = file.name;
          if (statusReadout) statusReadout.textContent = '';
          syncProperties();
          syncReadouts();
          redraw();
        };
        image.onerror = () => {
          URL.revokeObjectURL(nextUrl);
          uploadInput.value = '';
          state.artworkImage = null;
          state.artworkFileName = '';
          if (statusReadout) statusReadout.textContent = 'Image could not be loaded. Try another file.';
          syncProperties();
          syncReadouts();
          redraw();
        };
        image.src = nextUrl;
      });

      if (variantSelect) {
        variantSelect.addEventListener('change', syncVariant);
        syncVariant();
      }

      syncProperties();
      syncReadouts();
      redraw();

      form.addEventListener('submit', async (event) => {
        if (!form.reportValidity()) return;
        if (!variantIdInput || !variantIdInput.value) {
          event.preventDefault();
          if (statusReadout) statusReadout.textContent = 'No product is connected to this customizer.';
          return;
        }

        event.preventDefault();
        if (submitButton) submitButton.disabled = true;
        if (statusReadout) statusReadout.textContent = 'Generating front and back snapshots...';

        try {
          syncProperties();
          const timestamp = Date.now();
          const frontBlob = await canvasToBlob(frontCanvas);
          const backBlob = await canvasToBlob(backCanvas);
          const frontFile = new File([frontBlob], `front-preview-${timestamp}.png`, { type: 'image/png' });
          const backFile = new File([backBlob], `back-preview-${timestamp}.png`, { type: 'image/png' });
          assignFileInput(frontShotInput, frontFile);
          assignFileInput(backShotInput, backFile);
          if (statusReadout) statusReadout.textContent = 'Adding your custom design to cart...';
          form.submit();
        } catch (error) {
          console.error(error);
          if (statusReadout) statusReadout.textContent = 'Could not attach snapshots. Please try again.';
          if (submitButton) submitButton.disabled = false;
        }
      });

      window.addEventListener('pagehide', () => {
        if (artworkUrl) URL.revokeObjectURL(artworkUrl);
      }, { once: true });
    });
  }

  function initStudioV2Advanced() {
    const roots = document.querySelectorAll('[data-studio-root]');
    if (!roots.length) return;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const normalizeHex = (value) => {
      const raw = String(value || '').trim();
      const withHash = raw.startsWith('#') ? raw : `#${raw}`;
      return /^#[0-9a-fA-F]{6}$/.test(withHash) ? withHash.toUpperCase() : '';
    };
    const washLabel = (wash) => ({
      'acid-blue': 'Acid Wash Blue',
      'acid-black': 'Washed Black',
      'acid-green': 'Acid Wash Green',
      'acid-red': 'Acid Wash Red',
      solid: 'Solid'
    }[wash] || 'Solid');

    const defaultSizes = ['S', 'M', 'L', 'XL', '2XL'];
    const styleZones = {
      tshirt: {
        front: { cx: 0.5, cy: 0.54, w: 0.38, h: 0.26 },
        back: { cx: 0.5, cy: 0.50, w: 0.4, h: 0.3 }
      },
      polo: {
        front: { cx: 0.5, cy: 0.56, w: 0.35, h: 0.24 },
        back: { cx: 0.5, cy: 0.5, w: 0.38, h: 0.28 }
      },
      sweatshirt: {
        front: { cx: 0.5, cy: 0.55, w: 0.4, h: 0.24 },
        back: { cx: 0.5, cy: 0.5, w: 0.43, h: 0.29 }
      },
      hoodie: {
        front: { cx: 0.5, cy: 0.6, w: 0.4, h: 0.22 },
        back: { cx: 0.5, cy: 0.53, w: 0.42, h: 0.27 }
      }
    };

    roots.forEach((root) => {
      if (root.dataset.studioReadyAdvanced === 'true') return;
      root.dataset.studioReadyAdvanced = 'true';

      const form = root.querySelector('[data-studio-form]');
      const frontCanvas = root.querySelector('[data-studio-canvas="front"]');
      const backCanvas = root.querySelector('[data-studio-canvas="back"]');
      const frontCard = root.querySelector('[data-studio-preview-card="front"]');
      const backCard = root.querySelector('[data-studio-preview-card="back"]');
      const garmentSelect = root.querySelector('[data-studio-garment]');
      const sizeSelect = root.querySelector('[data-studio-size]');
      const sizePropertyInput = root.querySelector('[data-studio-size-property]');
      const swatches = root.querySelectorAll('[data-studio-swatch]');
      const customColorPicker = root.querySelector('[data-studio-custom-color-picker]');
      const customColorHex = root.querySelector('[data-studio-custom-color-hex]');
      const customColorApply = root.querySelector('[data-studio-custom-color-apply]');
      const uploadInput = root.querySelector('[data-studio-upload]');
      const uploadTrigger = root.querySelector('[data-studio-upload-trigger]');
      const uploadName = root.querySelector('[data-studio-upload-name]');
      const placementSelect = root.querySelector('[data-studio-placement]');
      const editSideButtons = root.querySelectorAll('[data-studio-edit-side]');
      const resetSideButton = root.querySelector('[data-studio-reset-side]');
      const garmentReadout = root.querySelector('[data-studio-garment-readout]');
      const sizeReadout = root.querySelector('[data-studio-size-readout]');
      const colorReadout = root.querySelector('[data-studio-color-readout]');
      const washReadout = root.querySelector('[data-studio-wash-readout]');
      const fileReadout = root.querySelector('[data-studio-file-readout]');
      const statusReadout = root.querySelector('[data-studio-status]');
      const colorProperty = root.querySelector('[data-studio-color-property]');
      const washProperty = root.querySelector('[data-studio-wash-property]');
      const colorSourceProperty = root.querySelector('[data-studio-color-source-property]');
      const customColorProperty = root.querySelector('[data-studio-custom-color-property]');
      const scaleProperty = root.querySelector('[data-studio-scale-property]');
      const artworkNameProperty = root.querySelector('[data-studio-artwork-name-property]');
      const frontTransformProperty = root.querySelector('[data-studio-front-transform-property]');
      const backTransformProperty = root.querySelector('[data-studio-back-transform-property]');
      const quotedPriceProperty = root.querySelector('[data-studio-quoted-price-property]');
      const variantIdInput = root.querySelector('[data-studio-variant-id]');
      const submitButton = root.querySelector('[data-studio-submit]');
      const priceReadout = root.querySelector('[data-studio-price]');
      const frontShotInput = root.querySelector('[data-studio-front-shot]');
      const backShotInput = root.querySelector('[data-studio-back-shot]');
      const productsScript = root.querySelector('[data-studio-products]');

      if (!form || !frontCanvas || !backCanvas || !uploadInput || !placementSelect) return;

      const fixedPrice = Number(root.dataset.fixedPrice || 899);
      const currencySymbol = root.dataset.currencySymbol || '₹';
      const parseProducts = () => {
        try {
          return JSON.parse(productsScript ? productsScript.textContent : '{}');
        } catch {
          return {};
        }
      };
      const productMap = parseProducts();

      const textureMap = {
        'acid-blue': root.dataset.washTextureAcidBlue || '',
        'acid-black': root.dataset.washTextureAcidBlack || '',
        'acid-green': root.dataset.washTextureAcidGreen || '',
        'acid-red': root.dataset.washTextureAcidRed || ''
      };

      const state = {
        garment: garmentSelect ? garmentSelect.value : 'tshirt',
        color: normalizeHex(root.dataset.defaultColor || '#30406F') || '#30406F',
        colorName: root.dataset.defaultColorName || 'Acid Wash Blue',
        wash: root.dataset.defaultWash || 'acid-blue',
        colorSource: 'Acid swatch',
        placement: placementSelect.value || 'both',
        artworkImage: null,
        artworkFileName: '',
        activeSide: 'front',
        transforms: {
          front: { x: 0, y: 0, scale: 1, rotate: 0 },
          back: { x: 0, y: 0, scale: 1, rotate: 0 }
        },
        drawMeta: { front: null, back: null }
      };

      let artworkUrl = '';
      let isDragging = false;
      let dragSide = null;
      let dragMode = null;
      let dragStartPoint = { x: 0, y: 0 };
      let dragStartTransform = null;
      let dragStartDistance = 0;

      const imageCache = new Map();

      const setStatus = (message) => {
        if (statusReadout) statusReadout.textContent = message || '';
      };

      const shouldRenderOnSide = (side) => state.placement === 'both' || state.placement === side;

      const getGarmentLabel = () => {
        if (!garmentSelect) return 'T-Shirt';
        const option = garmentSelect.options[garmentSelect.selectedIndex];
        return option ? option.textContent.trim() : 'T-Shirt';
      };

      const getGarmentConfig = () => productMap[state.garment] || productMap.tshirt || {};
      const toSizeLabel = (rawTitle) => {
        const source = String(rawTitle || '').trim();
        if (!source || source.toLowerCase() === 'default title') return '';
        const compact = source.split('/')[0].split('|')[0].replace(/\b\d+\s*GSM\b/ig, '').trim();
        return compact || source.replace(/\b\d+\s*GSM\b/ig, '').trim() || source;
      };

      const getLoadedImage = (url) => {
        if (!url) return null;
        if (imageCache.has(url)) {
          const entry = imageCache.get(url);
          return entry.status === 'loaded' ? entry.image : null;
        }

        const image = new Image();
        image.decoding = 'async';
        image.crossOrigin = 'anonymous';
        imageCache.set(url, { image, status: 'loading' });
        image.onload = () => {
          const entry = imageCache.get(url);
          if (entry) entry.status = 'loaded';
          redraw();
        };
        image.onerror = () => {
          const entry = imageCache.get(url);
          if (entry) entry.status = 'error';
        };
        image.src = url;
        return null;
      };

      const updateReadouts = () => {
        if (garmentReadout) garmentReadout.textContent = getGarmentLabel();
        if (sizeReadout && sizePropertyInput) sizeReadout.textContent = sizePropertyInput.value || 'Not selected';
        if (colorReadout) colorReadout.textContent = state.colorName;
        if (washReadout) washReadout.textContent = washLabel(state.wash);
        if (fileReadout) fileReadout.textContent = state.artworkFileName || 'No file uploaded';
        if (uploadName) uploadName.textContent = state.artworkFileName || 'No file selected';
        if (priceReadout) priceReadout.textContent = `${currencySymbol}${fixedPrice}`;
      };

      const syncProperties = () => {
        if (colorProperty) colorProperty.value = `${state.colorName} (${state.color})`;
        if (washProperty) washProperty.value = washLabel(state.wash);
        if (colorSourceProperty) colorSourceProperty.value = state.colorSource;
        if (customColorProperty) customColorProperty.value = state.colorSource === 'Custom color' ? state.color : '';
        if (artworkNameProperty) artworkNameProperty.value = state.artworkFileName || '';
        if (frontTransformProperty) frontTransformProperty.value = JSON.stringify(state.transforms.front);
        if (backTransformProperty) backTransformProperty.value = JSON.stringify(state.transforms.back);
        if (quotedPriceProperty) quotedPriceProperty.value = `${currencySymbol}${fixedPrice}`;
        const activeScale = state.transforms[state.activeSide] ? state.transforms[state.activeSide].scale : 1;
        if (scaleProperty) scaleProperty.value = `${Math.round(activeScale * 100)}%`;
      };

      const updateVariantFromSize = () => {
        if (!sizeSelect) return;
        const selected = sizeSelect.options[sizeSelect.selectedIndex];
        if (sizePropertyInput) sizePropertyInput.value = selected ? selected.value : '';
        if (variantIdInput && selected && selected.dataset.variantId) {
          variantIdInput.value = selected.dataset.variantId;
        } else if (variantIdInput) {
          const garmentConfig = getGarmentConfig();
          const variants = Array.isArray(garmentConfig.variants) ? garmentConfig.variants : [];
          const firstAvailable = variants.find((variant) => variant.available) || variants[0];
          if (firstAvailable) variantIdInput.value = String(firstAvailable.id);
        }
        if (submitButton) submitButton.disabled = !variantIdInput || !variantIdInput.value;
        updateReadouts();
        syncProperties();
      };

      const rebuildSizeOptions = () => {
        if (!sizeSelect) return;
        sizeSelect.innerHTML = '';

        const garmentConfig = getGarmentConfig();
        const variants = Array.isArray(garmentConfig.variants) ? garmentConfig.variants : [];
        let firstEnabledIndex = -1;

        if (variants.length) {
          variants.forEach((variant, index) => {
            const option = document.createElement('option');
            option.value = toSizeLabel(variant.title || `Option ${index + 1}`) || `Option ${index + 1}`;
            option.textContent = option.value;
            option.dataset.variantId = String(variant.id || '');
            if (!variant.available) option.disabled = true;
            if (firstEnabledIndex === -1 && variant.available) firstEnabledIndex = index;
            sizeSelect.appendChild(option);
          });
        } else {
          defaultSizes.forEach((size, index) => {
            const option = document.createElement('option');
            option.value = size;
            option.textContent = size;
            sizeSelect.appendChild(option);
            if (firstEnabledIndex === -1) firstEnabledIndex = index;
          });
        }

        if (firstEnabledIndex >= 0) sizeSelect.selectedIndex = firstEnabledIndex;
        updateVariantFromSize();
      };

      const setActiveSide = (side) => {
        state.activeSide = side === 'back' ? 'back' : 'front';
        editSideButtons.forEach((button) => {
          button.classList.toggle('is-active', button.dataset.studioEditSide === state.activeSide);
        });
        if (frontCard) frontCard.classList.toggle('is-active', state.activeSide === 'front');
        if (backCard) backCard.classList.toggle('is-active', state.activeSide === 'back');
        redraw();
      };

      const hexToRgba = (hex, alpha) => {
        const cleaned = normalizeHex(hex).replace('#', '');
        if (cleaned.length !== 6) return `rgba(0,0,0,${alpha})`;
        const r = parseInt(cleaned.slice(0, 2), 16);
        const g = parseInt(cleaned.slice(2, 4), 16);
        const b = parseInt(cleaned.slice(4, 6), 16);
        return `rgba(${r},${g},${b},${alpha})`;
      };

      const roundedRectPath = (ctx, x, y, w, h, r) => {
        const radius = Math.max(0, Math.min(r, Math.min(w, h) / 2));
        if (typeof ctx.roundRect === 'function') {
          ctx.beginPath();
          ctx.roundRect(x, y, w, h, radius);
          return;
        }
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + w - radius, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
        ctx.lineTo(x + w, y + h - radius);
        ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
        ctx.lineTo(x + radius, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
      };

      const drawGeneratedWash = (ctx, box, wash) => {
        const blobs = {
          'acid-blue': [
            { x: 0.28, y: 0.3, r: 0.18, c: 'rgba(224,236,255,.42)' },
            { x: 0.66, y: 0.63, r: 0.21, c: 'rgba(32,48,79,.36)' },
            { x: 0.46, y: 0.46, r: 0.17, c: 'rgba(157,182,214,.26)' }
          ],
          'acid-black': [
            { x: 0.28, y: 0.31, r: 0.18, c: 'rgba(235,235,235,.3)' },
            { x: 0.66, y: 0.62, r: 0.22, c: 'rgba(8,9,11,.45)' },
            { x: 0.45, y: 0.48, r: 0.16, c: 'rgba(170,176,188,.22)' }
          ],
          'acid-green': [
            { x: 0.28, y: 0.3, r: 0.18, c: 'rgba(232,240,216,.35)' },
            { x: 0.66, y: 0.64, r: 0.22, c: 'rgba(45,63,38,.38)' },
            { x: 0.46, y: 0.45, r: 0.16, c: 'rgba(199,214,180,.24)' }
          ],
          'acid-red': [
            { x: 0.27, y: 0.31, r: 0.18, c: 'rgba(255,223,225,.38)' },
            { x: 0.68, y: 0.63, r: 0.22, c: 'rgba(91,29,39,.4)' },
            { x: 0.46, y: 0.46, r: 0.16, c: 'rgba(221,159,169,.24)' }
          ]
        }[wash];
        if (!blobs) return;

        blobs.forEach((blob) => {
          const gradient = ctx.createRadialGradient(
            box.x + box.w * blob.x,
            box.y + box.h * blob.y,
            0,
            box.x + box.w * blob.x,
            box.y + box.h * blob.y,
            box.w * blob.r
          );
          gradient.addColorStop(0, blob.c);
          gradient.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(box.x + box.w * blob.x, box.y + box.h * blob.y, box.w * blob.r, 0, Math.PI * 2);
          ctx.fill();
        });
      };

      const getZone = (side, modelBox) => {
        const byStyle = styleZones[state.garment] || styleZones.tshirt;
        const zone = byStyle[side] || styleZones.tshirt[side];
        return {
          cx: modelBox.x + modelBox.w * zone.cx,
          cy: modelBox.y + modelBox.h * zone.cy,
          w: modelBox.w * zone.w,
          h: modelBox.h * zone.h
        };
      };

      const drawPreview = (canvas, side) => {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const width = canvas.width;
        const height = canvas.height;
        state.drawMeta[side] = null;

        ctx.clearRect(0, 0, width, height);
        const background = ctx.createLinearGradient(0, 0, 0, height);
        background.addColorStop(0, '#fdfdfd');
        background.addColorStop(1, '#efefef');
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, width, height);

        const colorBox = {
          x: width * 0.12,
          y: height * 0.12,
          w: width * 0.76,
          h: height * 0.78
        };

        ctx.save();
        roundedRectPath(ctx, colorBox.x, colorBox.y, colorBox.w, colorBox.h, 28);
        ctx.clip();
        ctx.fillStyle = state.color;
        ctx.fillRect(colorBox.x, colorBox.y, colorBox.w, colorBox.h);

        if (state.wash !== 'solid') {
          ctx.globalCompositeOperation = 'soft-light';
          const textureImage = getLoadedImage(textureMap[state.wash]);
          if (textureImage) {
            ctx.drawImage(textureImage, colorBox.x, colorBox.y, colorBox.w, colorBox.h);
          } else {
            drawGeneratedWash(ctx, colorBox, state.wash);
          }
          ctx.globalCompositeOperation = 'source-over';
        }

        const light = ctx.createLinearGradient(colorBox.x, colorBox.y, colorBox.x + colorBox.w, colorBox.y + colorBox.h);
        light.addColorStop(0, hexToRgba(state.color, .26));
        light.addColorStop(.42, 'rgba(255,255,255,0)');
        light.addColorStop(1, 'rgba(0,0,0,.24)');
        ctx.fillStyle = light;
        ctx.fillRect(colorBox.x, colorBox.y, colorBox.w, colorBox.h);
        ctx.restore();

        ctx.strokeStyle = 'rgba(0,0,0,.1)';
        ctx.lineWidth = 1.5;
        roundedRectPath(ctx, colorBox.x, colorBox.y, colorBox.w, colorBox.h, 28);
        ctx.stroke();

        const zone = getZone(side, colorBox);
        const transform = state.transforms[side];
        const shouldRender = shouldRenderOnSide(side);
        if (!shouldRender) {
          ctx.fillStyle = 'rgba(0,0,0,.38)';
          ctx.font = '600 14px Inter, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('No print', zone.cx, zone.cy);
        } else if (!state.artworkImage) {
          ctx.strokeStyle = 'rgba(0,0,0,.35)';
          ctx.lineWidth = 2;
          ctx.setLineDash([8, 6]);
          ctx.strokeRect(zone.cx - zone.w / 2, zone.cy - zone.h / 2, zone.w, zone.h);
          ctx.setLineDash([]);
          ctx.fillStyle = 'rgba(0,0,0,.42)';
          ctx.font = '600 13px Inter, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('Upload artwork', zone.cx, zone.cy + 6);
        } else {
          const ratio = state.artworkImage.width / state.artworkImage.height;
          let drawW = zone.w;
          let drawH = zone.h;
          if (ratio > 1) drawH = drawW / ratio;
          else drawW = drawH * ratio;
          drawW *= transform.scale;
          drawH *= transform.scale;

          const centerX = zone.cx + transform.x;
          const centerY = zone.cy + transform.y;
          const angle = transform.rotate * (Math.PI / 180);

          ctx.save();
          ctx.translate(centerX, centerY);
          ctx.rotate(angle);
          ctx.shadowColor = 'rgba(0,0,0,.24)';
          ctx.shadowBlur = 12;
          ctx.drawImage(state.artworkImage, -drawW / 2, -drawH / 2, drawW, drawH);

          if (state.activeSide === side) {
            ctx.shadowBlur = 0;
            ctx.strokeStyle = 'rgba(255,255,255,.92)';
            ctx.lineWidth = 2;
            ctx.setLineDash([6, 4]);
            ctx.strokeRect(-drawW / 2, -drawH / 2, drawW, drawH);
            ctx.setLineDash([]);
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.arc(drawW / 2, drawH / 2, 7, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.restore();

          state.drawMeta[side] = { centerX, centerY, drawW, drawH, angle };
        }

        ctx.fillStyle = 'rgba(18,18,18,.62)';
        ctx.font = '600 15px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(side === 'front' ? 'Front view' : 'Back view', width / 2, height - 16);
      };

      const redraw = () => {
        drawPreview(frontCanvas, 'front');
        drawPreview(backCanvas, 'back');
        updateReadouts();
        syncProperties();
        const activeCanvas = state.activeSide === 'front' ? frontCanvas : backCanvas;
        const activeMeta = state.drawMeta[state.activeSide];
        frontCanvas.classList.toggle('is-editable', activeCanvas === frontCanvas && !!activeMeta);
        backCanvas.classList.toggle('is-editable', activeCanvas === backCanvas && !!activeMeta);
      };

      const toCanvasPoint = (event, canvas) => {
        const rect = canvas.getBoundingClientRect();
        return {
          x: ((event.clientX - rect.left) * canvas.width) / rect.width,
          y: ((event.clientY - rect.top) * canvas.height) / rect.height
        };
      };

      const toLocalPoint = (point, meta) => {
        const dx = point.x - meta.centerX;
        const dy = point.y - meta.centerY;
        const cos = Math.cos(-meta.angle);
        const sin = Math.sin(-meta.angle);
        return {
          x: dx * cos - dy * sin,
          y: dx * sin + dy * cos
        };
      };

      const detectHit = (side, point) => {
        const meta = state.drawMeta[side];
        if (!meta) return '';
        const local = toLocalPoint(point, meta);
        const handleDistance = Math.hypot(local.x - meta.drawW / 2, local.y - meta.drawH / 2);
        if (handleDistance <= 14) return 'scale';
        if (Math.abs(local.x) <= meta.drawW / 2 && Math.abs(local.y) <= meta.drawH / 2) return 'move';
        return '';
      };

      const onCanvasPointerDown = (event, side, canvas) => {
        setActiveSide(side);
        if (!state.artworkImage || !shouldRenderOnSide(side)) return;

        const point = toCanvasPoint(event, canvas);
        const hit = detectHit(side, point);
        if (!hit) return;

        const transform = state.transforms[side];
        dragSide = side;
        dragMode = hit;
        isDragging = true;
        dragStartPoint = point;
        dragStartTransform = { ...transform };
        if (hit === 'scale') {
          const meta = state.drawMeta[side];
          const local = toLocalPoint(point, meta);
          dragStartDistance = Math.max(12, Math.hypot(local.x, local.y));
        }
        canvas.classList.add('is-dragging');
        if (canvas.setPointerCapture) canvas.setPointerCapture(event.pointerId);
        event.preventDefault();
      };

      const stopDragging = () => {
        if (!isDragging) return;
        isDragging = false;
        dragMode = null;
        dragSide = null;
        frontCanvas.classList.remove('is-dragging');
        backCanvas.classList.remove('is-dragging');
      };

      window.addEventListener('pointermove', (event) => {
        if (!isDragging || !dragSide || !dragMode) return;
        const canvas = dragSide === 'front' ? frontCanvas : backCanvas;
        const point = toCanvasPoint(event, canvas);
        const transform = state.transforms[dragSide];

        if (dragMode === 'move') {
          transform.x = clamp(dragStartTransform.x + (point.x - dragStartPoint.x), -260, 260);
          transform.y = clamp(dragStartTransform.y + (point.y - dragStartPoint.y), -260, 260);
        } else if (dragMode === 'scale') {
          const meta = state.drawMeta[dragSide];
          if (!meta) return;
          const local = toLocalPoint(point, meta);
          const distance = Math.max(12, Math.hypot(local.x, local.y));
          transform.scale = clamp(dragStartTransform.scale * (distance / dragStartDistance), .4, 2.4);
        }
        redraw();
      }, { passive: true });

      window.addEventListener('pointerup', stopDragging);
      window.addEventListener('pointercancel', stopDragging);

      frontCanvas.addEventListener('pointerdown', (event) => onCanvasPointerDown(event, 'front', frontCanvas));
      backCanvas.addEventListener('pointerdown', (event) => onCanvasPointerDown(event, 'back', backCanvas));
      if (frontCard) frontCard.addEventListener('click', () => setActiveSide('front'));
      if (backCard) backCard.addEventListener('click', () => setActiveSide('back'));

      if (garmentSelect) {
        garmentSelect.addEventListener('change', () => {
          state.garment = garmentSelect.value || 'tshirt';
          rebuildSizeOptions();
          redraw();
        });
      }

      if (sizeSelect) {
        sizeSelect.addEventListener('change', () => {
          updateVariantFromSize();
          redraw();
        });
      }

      swatches.forEach((button) => {
        button.addEventListener('click', () => {
          swatches.forEach((other) => other.classList.remove('is-active'));
          button.classList.add('is-active');
          const hex = normalizeHex(button.dataset.color || state.color);
          state.color = hex || state.color;
          state.colorName = button.dataset.colorName || state.colorName;
          state.wash = button.dataset.wash || 'solid';
          state.colorSource = 'Acid swatch';
          if (customColorPicker) customColorPicker.value = state.color;
          if (customColorHex) customColorHex.value = state.color;
          redraw();
        });
      });

      const applyCustomColor = (hexValue) => {
        const normalized = normalizeHex(hexValue);
        if (!normalized) {
          setStatus('Enter a valid HEX color like #A37C9B');
          return;
        }
        swatches.forEach((swatch) => swatch.classList.remove('is-active'));
        state.color = normalized;
        state.colorName = `Custom ${normalized}`;
        state.wash = 'solid';
        state.colorSource = 'Custom color';
        if (customColorPicker) customColorPicker.value = normalized;
        if (customColorHex) customColorHex.value = normalized;
        setStatus('');
        redraw();
      };

      if (customColorApply && customColorHex) {
        customColorApply.addEventListener('click', () => applyCustomColor(customColorHex.value));
      }
      if (customColorPicker) {
        customColorPicker.addEventListener('input', () => applyCustomColor(customColorPicker.value));
      }
      if (customColorHex) {
        customColorHex.addEventListener('change', () => applyCustomColor(customColorHex.value));
        customColorHex.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          applyCustomColor(customColorHex.value);
        });
      }

      if (uploadTrigger) {
        uploadTrigger.addEventListener('click', () => uploadInput.click());
      }

      placementSelect.addEventListener('change', () => {
        state.placement = placementSelect.value || 'both';
        redraw();
      });

      editSideButtons.forEach((button) => {
        button.addEventListener('click', () => setActiveSide(button.dataset.studioEditSide));
      });
      if (resetSideButton) {
        resetSideButton.addEventListener('click', () => {
          state.transforms[state.activeSide] = { x: 0, y: 0, scale: 1, rotate: 0 };
          redraw();
        });
      }

      uploadInput.addEventListener('change', () => {
        const file = uploadInput.files && uploadInput.files[0];
        if (!file) {
          state.artworkImage = null;
          state.artworkFileName = '';
          redraw();
          return;
        }

        if (!file.type || !file.type.startsWith('image/')) {
          uploadInput.value = '';
          state.artworkImage = null;
          state.artworkFileName = '';
          setStatus('Please upload a valid image file.');
          redraw();
          return;
        }

        const nextUrl = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
          if (artworkUrl) URL.revokeObjectURL(artworkUrl);
          artworkUrl = nextUrl;
          state.artworkImage = image;
          state.artworkFileName = file.name;
          setStatus('');
          redraw();
        };
        image.onerror = () => {
          URL.revokeObjectURL(nextUrl);
          uploadInput.value = '';
          state.artworkImage = null;
          state.artworkFileName = '';
          setStatus('Image could not be loaded. Try another file.');
          redraw();
        };
        image.src = nextUrl;
      });

      const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Unable to generate snapshot image.'));
            return;
          }
          resolve(blob);
        }, 'image/png', .92);
      });

      const assignFileInput = (input, file) => {
        if (!input || typeof DataTransfer === 'undefined') {
          throw new Error('Snapshot upload is not supported in this browser.');
        }
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        input.files = dataTransfer.files;
      };

      form.addEventListener('submit', async (event) => {
        if (!form.reportValidity()) return;
        if (!variantIdInput || !variantIdInput.value) {
          event.preventDefault();
          setStatus('No valid checkout variant found for the selected garment/size.');
          return;
        }

        event.preventDefault();
        if (submitButton) submitButton.disabled = true;
        setStatus('Generating front and back snapshots...');

        try {
          syncProperties();
          const stamp = Date.now();
          const frontBlob = await canvasToBlob(frontCanvas);
          const backBlob = await canvasToBlob(backCanvas);
          assignFileInput(frontShotInput, new File([frontBlob], `front-preview-${stamp}.png`, { type: 'image/png' }));
          assignFileInput(backShotInput, new File([backBlob], `back-preview-${stamp}.png`, { type: 'image/png' }));
          setStatus('Adding your custom design to cart...');
          form.submit();
        } catch (error) {
          console.error(error);
          setStatus('Could not attach snapshots. Please try again.');
          if (submitButton) submitButton.disabled = false;
        }
      });

      rebuildSizeOptions();
      setActiveSide('front');
      redraw();

      window.addEventListener('pagehide', () => {
        if (artworkUrl) URL.revokeObjectURL(artworkUrl);
      }, { once: true });
    });
  }

  /* ─── BOOT ─── */
  function boot() {
    const reducedMotion = prefersReduced();

    // Keep customizer controls active even when reduced-motion is enabled
    SuperDesign.init({ reducedMotion });
    initStudioV2Advanced();
    if (reducedMotion) return;

    // Desktop-only effects
    if (!isMobile()) {
      Tilt.init('.brand-pillar-card', { maxTilt: 5, scale: 1.01, glare: false });
      Tilt.init('.collection-showcase-item', { maxTilt: 4, scale: 1.01, glare: true, maxGlare: .08 });
      Tilt.init('.lookbook-card', { maxTilt: 6, scale: 1.01, glare: true, maxGlare: .1 });
      Parallax.init();
      Magnetic.init('.button-gold, .collection-showcase-cta, .footer-icon-link', .2);
      Cursor.init();
      Shine.init();
    }

    // Universal effects
    ScrollReveal3D.init();
    Particles.init();
    Counter.init();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
